import { useEffect, useId, useRef, useState } from 'react';
import { useHuddle } from '../huddle/HuddleProvider';
import { useActiveHuddles } from '../data/huddles';
import { closeCallNotifications } from '../lib/call-notifications';
import { openHuddleWindow } from '../lib/huddle-window';
import {
  onHuddleCancelled,
  onHuddleInvited,
  onHuddleUpdated,
  type HuddleInvite,
} from '../huddle/bus';
import { useSession } from '../lib/session';
import { focusDesktopWindow } from '../lib/desktop';
import { pushDeliversHere } from '../lib/push-device';
import { useNotificationPrefs } from '../data/notifications';
import { useUsers } from '../data/users';
import { startRingtone, stopRingtone } from '../lib/sound';
import { handleServiceCall, serviceHandlesCalls } from '../lib/service-call';
import { DEFAULT_RINGTONE } from '@avslack/shared';
import { Avatar } from './Avatar';
import { HuddleTimer } from './HuddleTimer';
import { Icon } from './Icon';

/**
 * A FO ablak huddle-savja.
 *
 * A hivast a kulon ablak tartja, ezert itt nincs media-vezerlo - csak az
 * allapot: bent vagyok-e, es ha igen, hol. A "Vissza az ablakhoz" gomb
 * elohozza (vagy ujranyitja) a huddle ablakot.
 */
/** Hova huztuk a savot? A helye tullep egy ujratoltest. */
const POSITION_KEY = 'avslack.huddle-bar.position';

interface Position {
  x: number;
  y: number;
}

function readPosition(): Position | null {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    return raw ? (JSON.parse(raw) as Position) : null;
  } catch {
    return null;
  }
}

function savePosition(position: Position | null): void {
  try {
    if (position) localStorage.setItem(POSITION_KEY, JSON.stringify(position));
    else localStorage.removeItem(POSITION_KEY);
  } catch {
    // a tarolas hianya nem torheti el a savot
  }
}

/** A savnak a kepernyon KELL maradnia: kicsuszva visszahozhatatlan lenne. */
function clampPosition(x: number, y: number, box: HTMLElement | null): Position {
  const width = box?.offsetWidth ?? 320;
  const height = box?.offsetHeight ?? 64;
  return {
    x: Math.min(Math.max(8, x), window.innerWidth - width - 8),
    y: Math.min(Math.max(8, y), window.innerHeight - height - 8),
  };
}

const ARROW_STEPS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

export function HuddleBar({ meId }: { meId: string }) {
  const { error, dismissError } = useHuddle();
  const { data: huddles } = useActiveHuddles();

  const [position, setPosition] = useState<Position | null>(readPosition);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  /**
   * A sav MEGFOGHATO es athelyezheto.
   *
   * Alapbol bal alul all - eppen ott, ahol a sajat profil sora van, amit igy
   * kitakart. Ahelyett, hogy kitalalnank egy "mindenkinek jo" helyet, a
   * felhasznalora bizzuk, es megjegyezzuk a dontesét.
   */
  useEffect(() => {
    if (!position) return;

    const onMove = (event: PointerEvent) => {
      const held = drag.current;
      if (!held) return;
      setPosition(clampPosition(event.clientX - held.dx, event.clientY - held.dy, boxRef.current));
    };

    const onUp = () => {
      if (!drag.current) return;
      drag.current = null;
      savePosition(position);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [position]);

  const startDrag = (event: React.PointerEvent) => {
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    drag.current = { dx: event.clientX - rect.left, dy: event.clientY - rect.top };
    setPosition({ x: rect.left, y: rect.top });
  };

  /*
    A fogantyu BILLENTYUZETTEL is mozgat: a nyilak 16 px-et, Shifttel 64-et.
    A Home es az Escape visszateszi az alaphelyre (bal alul) - aki elrontotta,
    ne kelljen egerert nyulnia.
  */
  const moveByKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Home' || event.key === 'Escape') {
      // Ha mar alaphelyen all, az Escape menjen tovabb (pl. egy nyitott panelnek).
      if (!position) return;
      event.preventDefault();
      setPosition(null);
      savePosition(null);
      return;
    }
    const direction = ARROW_STEPS[event.key];
    const box = boxRef.current;
    if (!direction || !box) return;
    event.preventDefault();
    const step = event.shiftKey ? 64 : 16;
    const rect = box.getBoundingClientRect();
    const base = position ?? { x: rect.left, y: rect.top };
    const next = clampPosition(base.x + direction[0] * step, base.y + direction[1] * step, box);
    setPosition(next);
    savePosition(next);
  };

  /**
   * MINDEN hivas, amiben bent vagyok - nem csak az elso.
   *
   * A szunet ota lehet egyszerre tobb: egy elo es tetszoleges szamu
   * szuneteltetett. Ha itt csak egyet mutatnank, a szuneteltetett hivas
   * lathatatlanna valna - bent maradnank egy megbeszelesben, amirol a
   * felulet nem szol. Pontosan az a hibaosztaly, ami miatt a projekt elindult.
   */
  const mine = (huddles ?? []).filter((huddle) =>
    huddle.participants.some(
      (participant) => participant.user.id === meId && participant.state === 'joined',
    ),
  );

  if (mine.length === 0 && !error) return null;

  return (
    <div
      className={
        position
          ? 'pointer-events-none fixed z-40'
          : 'pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3 sm:left-3 sm:right-auto sm:justify-start'
      }
      style={position ? { left: position.x, top: position.y } : undefined}
    >
      <div
        ref={boxRef}
        className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-card border border-border bg-surface-raised shadow-modal"
      >
        {mine.map((huddle, index) => {
          const paused = huddle.participants.some((p) => p.user.id === meId && p.isPaused);

          return (
            <div
              key={huddle.id}
              className={`flex items-center gap-2.5 px-3.5 py-2.5${
                index > 0 ? ' border-t border-border-soft' : ''
              }`}
            >
              {/*
                Kulon fogantyu, nem az egesz doboz: kulonben a gombokra
                kattintas is huzasnak szamitana. Csak az elso soron - a doboz
                egeszet mozgatja.
              */}
              {index === 0 ? (
                <button
                  type="button"
                  onPointerDown={startDrag}
                  onKeyDown={moveByKey}
                  title="Fogd meg és húzd arrébb, vagy mozgasd a nyilakkal (Home: vissza a helyére)"
                  aria-label="Az ölelés-sáv áthelyezése: nyilakkal mozgasd, Home-mal vissza a helyére"
                  aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Home Escape"
                  className="shrink-0 cursor-grab touch-none rounded-control px-1 py-1.5 text-ink-faint transition-colors hover:bg-surface-hover hover:text-ink active:cursor-grabbing"
                >
                  <Icon name="fogantyu" size="sm" />
                </button>
              ) : (
                <span className="w-[1.6rem] shrink-0" aria-hidden />
              )}

              <Icon name={paused ? 'szunet' : 'huddle'} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {paused ? 'Szüneteltetett ölelés' : 'Ölelésben vagy'}
                </p>
                <p className="truncate text-2xs text-ink-faint">
                  {/*
                    Az ora itt is ott van, nem csak a hivas-ablakban: aki
                    visszajott dolgozni a fo ablakba, annak epp itt kell
                    latnia, mennyi ideje tart a hivas.
                  */}
                  <HuddleTimer
                    startedAt={huddle.startedAt}
                    className="font-medium text-ink-muted"
                  />{' '}
                  · {paused ? 'Nem hallanak és nem hallod őket' : 'A hívás a külön ablakban fut'} ·{' '}
                  {huddle.participants.filter((p) => p.state === 'joined').length} fő
                </p>
              </div>

              <button
                type="button"
                // Mar bent vagyunk: csak elohozzuk. Ujranavigalva ujratoltodne,
                // ami uj munkamenet - es a szerver kileptetne a regit.
                onClick={() => void openHuddleWindow(huddle.channelId, { focusExisting: true })}
                className="shrink-0 rounded-control bg-accent px-2.5 py-1 text-xs font-semibold text-accent-ink transition-colors hover:bg-accent-hover"
              >
                Ablak
              </button>
            </div>
          );
        })}

        {error && (
          <div className="border-t border-border-soft bg-[var(--danger-soft)] px-3.5 py-2.5">
            <p role="alert" className="text-sm text-ink">
              {error}
            </p>
            <button
              type="button"
              onClick={dismissError}
              className="mt-1.5 rounded-control bg-surface-raised px-2.5 py-1 text-xs font-medium transition-colors duration-(--duration-quick) hover:bg-surface-hover"
            >
              Értem
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Behivas-ertesites.
 *
 * A behivott mar latszik a huddle resztvevoi kozott "hivjuk" allapotban; ez
 * az, ami NALA csengetni fog. Szandekosan feltuno es kozepen van: egy hivast
 * konnyu elnezni egy sarokban felvillano savban.
 */
export function HuddleInvitePrompt() {
  const [invite, setInvite] = useState<HuddleInvite | null>(null);
  /** Kinyilt-e a hivas-ablak. Ha igen, ott csorog es ott dontenek. */
  const [inWindow, setInWindow] = useState(false);
  const meId = useSession().data?.user.id;
  const { data: prefs } = useNotificationPrefs();
  const { usersById } = useUsers();

  /*
    Behivaskor MAGATOL nyilik a hivas-ablak.

    Ugyanaz az ablak, amiben a beszelgetes zajlani fog: a hivott egybol latja,
    nem kell keresgelnie. A bongeszo viszont blokkolhatja a felugro ablakot, ha
    a megnyitas nem kozvetlen kattintasbol indul - ilyenkor visszaesunk erre a
    feluletre, hogy a hivas SOSE tunjon el nemán.
  */
  useEffect(
    () =>
      onHuddleInvited((next) => {
        setInvite(next);
        /*
          VEVOSZOLGALATI MOD: ha a lebego panel nyitva van, ott csorog a hivas - azonnal,
          szinkron eldontjuk, hogy a csengohang es a rendszer-ertesites meg se induljon
          (kulonben egy pillanatra duplan szolna).
        */
        if (handleServiceCall({ channelId: next.huddle.channelId, incoming: true })) {
          setInWindow(true);
          return;
        }
        void openHuddleWindow(next.huddle.channelId, { incoming: true }).then(setInWindow);
      }),
    [],
  );

  /*
    MASHOL vettuk fel (masik ablak, telepitett alkalmazas, telefon): itt
    abba kell hagyni a csorgest. Enelkul a 60 mp-es idozito allitotta le -
    addig a hivas alatt, sot a vege utan is szolt.
  */
  useEffect(
    () =>
      onHuddleUpdated((huddle) =>
        setInvite((current) => {
          if (current?.huddle.id !== huddle.id) return current;
          const joined = huddle.participants.some(
            (p) => p.user.id === meId && p.state === 'joined',
          );
          if (!joined) return current;
          setInWindow(false);
          closeCallNotifications(huddle.channelId);
          return null;
        }),
      ),
    [meId],
  );

  /**
   * A hivo letette, mielott felvettuk volna.
   *
   * Enelkul a csengohang addig szolna, amig a sajat idozitoje le nem jar - a
   * masik oldalon rege vege a hivasnak, es a hivott egy nem letezo hivast hall.
   */
  useEffect(
    () =>
      onHuddleCancelled((huddleId) =>
        setInvite((current) => {
          if (current?.huddle.id !== huddleId) return current;
          setInWindow(false);
          // A hivo letette: a fiokban se maradjon felveheto hivas.
          closeCallNotifications(current.huddle.channelId);
          return null;
        }),
      ),
    [],
  );

  /**
   * A hivas SZOL is, es a rendszer szintjen is jelez.
   *
   * A hang azert kell, mert egy felugro ablakot konnyu elnezni, ha eppen mas
   * ablakban dolgozunk - es eppen AKKOR a legfontosabb, amikor nem az AVSlack
   * van elorben. A rendszer-ertesites pedig akkor is latszik, ha a ful
   * hatterben van, vagy a bongeszo ikonja a talcan ul.
   */
  useEffect(() => {
    if (!invite) return;

    // A hivas-ablakban szol a csengohang; ket helyrol egyszerre visszhangozna.
    if (!inWindow) startRingtone(prefs?.ringtone ?? DEFAULT_RINGTONE);

    /*
      A rendszer-ertesites MINDIG megy, akkor is, ha az ablak kinyilt.

      Ha a bongeszo nincs elotérben - eppen akkor a legfontosabb -, a felugro
      ablak konnyen eltunhet mas ablakok mogott. Az ertesites a talcan is
      latszik, es kattintasra elohozza a hivast.
    */
    let native: Notification | null = null;
    /*
      A bejovo telefonhivasrol push is megy. Ahol az megerkezik, ott a mienk
      csak a masodik peldany lenne - az iOS nem vonja ossze oket.
    */
    const pushCovers = invite.pushed === true && pushDeliversHere();
    // A vevoszolgalati lebego panel mar jelez (es csenget): a rendszer-ertesites csak duplazna.
    const panelRings = inWindow && serviceHandlesCalls();
    if (
      !pushCovers &&
      !panelRings &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      try {
        native = new Notification(`${invite.invitedByName} hív`, {
          body: invite.channelName ? `#${invite.channelName}` : 'Közvetlen ölelés',
          tag: `huddle-${invite.huddle.id}`,
          // A rendszer sajat hangja helyett a valasztott csengohang szol.
          silent: true,
          requireInteraction: true,
        });
        native.addEventListener('click', () => {
          window.focus();
          void focusDesktopWindow();
        });
      } catch {
        // Androidon a konstruktor hibat dob; ott a push hozza az ertesitest.
        native = null;
      }
    }

    return () => {
      stopRingtone();
      native?.close();
    };
  }, [invite, inWindow, prefs?.ringtone]);

  /**
   * A hivas nem varhat orokke.
   *
   * Ha senki nem valaszol egy percen belul, elrejtjuk - az oleles amugy is
   * lezarul, ha nem lep be senki, es egy ott ragadt "csatlakozol?" kerdes
   * rosszabb, mint ha nem latszana.
   */
  useEffect(() => {
    if (!invite) return;
    const timer = setTimeout(() => {
      setInvite(null);
      setInWindow(false);
    }, 60_000);
    return () => clearTimeout(timer);
  }, [invite]);

  const decline = () => {
    stopRingtone();
    setInvite(null);
  };
  const declineRef = useRef(decline);
  declineRef.current = decline;

  const open = invite !== null && !inWindow;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const acceptRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const detailId = useId();

  /*
    Ez MODALIS, ezert itt szabad a fokuszt elvenni: a "Felveszem" kapja, es
    bezaraskor visszaker oda, ahol a felhasznalo dolgozott. Az Escape az
    "Elutasitom"-mal egyenerteku - az is csak a helyi felkinalast zarja,
    a hivas a tobbieknek megy tovabb.
  */
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    acceptRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // A mogotte levo panelek (useDismiss, Radix) ne zarodjanak be ugyanerre.
      event.preventDefault();
      event.stopPropagation();
      declineRef.current();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      previous?.focus();
    };
  }, [open]);

  // A Tab a ket gomb kozott korbe jar, nem szokik ki a sotet reteg ala.
  const trapTab = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Ha a hivas sajat ablakot kapott, itt nem mutatunk masodik feluletet.
  if (!invite || inWindow) return null;

  const caller = [...usersById.values()].find((user) => user.displayName === invite.invitedByName);
  const joined = invite.huddle.participants.filter((p) => p.state === 'joined');

  return (
    /*
      A hivas KOZEPEN es nagyban jelenik meg, sotetitett hatterrel.
      Egy sarokban felvillano sav pont akkor vesz el, amikor a legfontosabb
      lenne: amikor a felhasznalo epp mas dolgozik.
    */
    <div className="animate-fade-in fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-[2px]">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={detailId}
        onKeyDown={trapTab}
        className="animate-fade-rise w-full max-w-sm rounded-card border border-accent/40 bg-surface-raised p-6 text-center shadow-modal"
      >
        <div className="mx-auto mb-4 w-fit">
          {caller ? (
            <Avatar
              userId={caller.id}
              name={caller.displayName}
              avatarUrl={caller.avatarUrl}
              size="fill"
              className="size-28 rounded-3xl text-3xl"
            />
          ) : (
            <span className="grid size-28 place-items-center rounded-3xl bg-accent-soft text-4xl text-accent">
              <Icon name="huddle" />
            </span>
          )}
        </div>

        <p id={titleId} className="break-words font-display text-lg font-semibold">
          {invite.invitedByName}
        </p>
        <p id={detailId} className="mt-0.5 text-sm text-ink-muted">
          {invite.channelName ? `#${invite.channelName}` : 'Közvetlen ölelés'}
          {joined.length > 0 && ` · ${joined.length} fő bent`}
        </p>

        <div className="mt-6 flex gap-2">
          <button
            ref={acceptRef}
            type="button"
            onClick={() => {
              stopRingtone();
              void openHuddleWindow(invite.huddle.channelId);
              setInvite(null);
            }}
            className="flex flex-1 items-center justify-center gap-2 rounded-control bg-accent px-3 py-3 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-hover"
          >
            <Icon name="huddle" /> Felveszem
          </button>
          <button
            type="button"
            onClick={decline}
            className="flex flex-1 items-center justify-center gap-2 rounded-control bg-[var(--danger)] px-3 py-3 text-sm font-semibold text-white transition-transform hover:brightness-110 active:scale-95"
          >
            <Icon name="kilepes" /> Elutasítom
          </button>
        </div>
      </div>
    </div>
  );
}
